import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DX_USD_CURRENCY_FORMAT, Messages, ProductionReconciliationMetricKey } from '@ag-common-lib/public-api';
import { type ValidationCallbackData } from 'devextreme/common';
import { DxFormComponent } from 'devextreme-angular';
import { type ValueChangedEvent } from 'devextreme/ui/date_box';
import { type CellPreparedEvent } from 'devextreme/ui/data_grid';
import {
  buildIsoDate,
  getEndOfYearDate,
  getLastPassedFriday,
  normalizeAsOfDate,
  toDateParts,
  toIsoDate,
} from 'ag-common-svc/lib/utils/reporting-comparison-date.utils';
import { PRODUCTION_DASHBOARD_CONTEXT } from '../production-dashboard-context';
import {
  RECONCILIATION_MODE_OPTIONS,
  ProductionReconciliationDashboardColumnKey,
  ProductionReconciliationDashboardSetup,
  ProductionReconciliationDualValue,
  ProductionReconciliationPresetMode,
  ProductionReconciliationSelectOption,
  buildReportingPeriodOptionLabel,
} from './production-reconciliation-dashboard.models';
import { ProductionReconciliationDashboardService } from './production-reconciliation-dashboard.service';
import {
  formatCompactDateRange,
  getLatestCompletedReportingPeriodIndex,
  getReportingPeriodIndexForDate,
  getReportingPeriodIndices,
  getReportingPeriodRange,
} from './reporting-week.utils';

type ReconciliationGridColumn = {
  dataField?: string;
  caption: string;
  dataType?: 'string' | 'number';
  fixed?: boolean;
  fixedPosition?: 'left';
  minWidth?: number;
  width?: number;
  alignment?: 'left' | 'center' | 'right';
  sortOrder?: 'asc';
  sortIndex?: number;
  columns?: ReconciliationGridColumn[];
  cellTemplate?: string;
  metricFormat?: 'currency' | 'number';
};

type ReconciliationMetricCellViewModel = {
  primary: string;
  change: string | null;
};

type ReconciliationGridSummaryTotalItem = {
  dataField: string;
  label: string;
  format: 'currency' | 'number';
};

type ReconciliationGridSummaryAccumulator = {
  current: number;
  compare: number;
  hasCurrent: boolean;
  hasCompare: boolean;
};

type ReconciliationGridCustomSummaryOptions = {
  name?: string;
  summaryProcess?: 'start' | 'calculate' | 'finalize' | string;
  value?: ProductionReconciliationDualValue | string | null;
  totalValue?: ReconciliationGridSummaryAccumulator | string;
};

type DateBoxValue = Date | string | number | null | undefined;

@Component({
  selector: 'ag-shr-production-reconciliation-dashboard',
  templateUrl: './production-reconciliation-dashboard.component.html',
  styleUrls: ['./production-reconciliation-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductionReconciliationDashboardService],
})
export class ProductionReconciliationDashboardComponent {
  @ViewChild('setupFormRef', { static: false }) private setupFormComponent?: DxFormComponent;

  protected readonly productionReconciliationDashboardColumnKey = ProductionReconciliationDashboardColumnKey;
  protected readonly setupValidationGroup = 'productionReconciliationSetupValidationGroup';
  protected readonly reconciliationModeOptions = RECONCILIATION_MODE_OPTIONS;
  protected readonly DX_USD_CURRENCY_FORMAT = DX_USD_CURRENCY_FORMAT;

  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dashboardContext = inject(PRODUCTION_DASHBOARD_CONTEXT);
  private readonly reconciliationService = inject(ProductionReconciliationDashboardService);
  private readonly yearsSig = toSignal(this.dashboardContext.years$, { initialValue: [] as number[] });
  private hasInitializedSetupFromAsOfDate = false;

  protected isLoadingSig = signal(false);
  protected isSetupModalVisible = false;
  protected isPeriodDropDownOpenSig = signal(false);
  protected loadErrorMessageSig = signal<string | null>(null);
  protected appliedSetup: ProductionReconciliationDashboardSetup = this.createDefaultSetup();
  protected draftSetup: ProductionReconciliationDashboardSetup = this.createDefaultSetup();
  protected dashboardDataSig = signal<Awaited<ReturnType<ProductionReconciliationDashboardService['loadDashboardData']>> | null>(
    null,
  );

  protected readonly availableYearsSig = computed<number[]>(() => {
    const years = [...(this.yearsSig() ?? [])].filter(Boolean).sort((left, right) => right - left);
    const currentYear = new Date().getFullYear();
    const fallbackYears = Array.from({ length: 6 }, (_, index) => currentYear - index);
    const combined = years.length ? [...new Set([currentYear, ...years, ...fallbackYears])] : fallbackYears;

    return combined.sort((left, right) => right - left);
  });

  protected readonly yearOptionsSig = computed<ProductionReconciliationSelectOption<number>[]>(() =>
    this.availableYearsSig().map(year => ({ id: year, text: `${year}` })),
  );

  protected readonly draftPeriodOptionsSig = computed<ProductionReconciliationSelectOption<number>[]>(() => {
    const year = this.isSetupModalVisible ? this.draftSetup.currentYear : this.appliedSetup.currentYear;
    const periodIndices = getReportingPeriodIndices(year);
    console.log('periodIndices', periodIndices);
    return periodIndices.map(periodIndex => ({
      id: periodIndex,
      text: buildReportingPeriodOptionLabel(year, periodIndex),
    }));
  });

  protected readonly validateDraftYearsAreDifferent = (_event?: ValidationCallbackData): boolean =>
    this.draftSetup.currentYear !== this.draftSetup.compareYear;

  protected readonly validateDraftPeriodsRequired = (_event?: ValidationCallbackData): boolean => {
    if (this.draftSetup.mode !== 'range') {
      return true;
    }

    return this.draftSetup.currentYearPeriods.length > 0;
  };

  protected readonly gridColumnsSig = computed<ReconciliationGridColumn[]>(() => {
    const dashboardData = this.dashboardDataSig();

    if (!dashboardData) {
      return this.buildCarrierColumn();
    }

    return [
      ...this.buildCarrierColumn(),
      ...dashboardData.periods.map(period => ({
        caption: period.caption,
        alignment: 'center' as const,
        columns: period.metrics.map(metric => ({
          dataField: metric.dataField,
          caption: metric.label,
          dataType: 'number' as const,
          alignment: 'right' as const,
          minWidth: 150,
          cellTemplate: 'reconciliationMetricCell',
          metricFormat: metric.format,
        })),
      })),
    ];
  });

  protected readonly gridDataSourceSig = computed(() => this.dashboardDataSig()?.rows ?? []);

  protected readonly gridSummaryTotalItemsSig = computed<ReconciliationGridSummaryTotalItem[]>(() => {
    const dashboardData = this.dashboardDataSig();

    if (!dashboardData) {
      return [];
    }

    return dashboardData.periods.flatMap(period =>
      period.metrics.map(metric => ({
        dataField: metric.dataField,
        label: metric.key === ProductionReconciliationMetricKey.submitted ? 'Total Submitted' : 'Total Paid',
        format: metric.format,
      })),
    );
  });

  protected readonly calculateReconciliationGridCustomSummary = (
    options: ReconciliationGridCustomSummaryOptions,
  ): void => {
    const summaryItem = this.gridSummaryTotalItemsSig().find(item => item.dataField === options.name);

    if (!summaryItem) {
      return;
    }

    if (options.summaryProcess === 'start') {
      options.totalValue = {
        current: 0,
        compare: 0,
        hasCurrent: false,
        hasCompare: false,
      };
      return;
    }

    if (options.summaryProcess === 'calculate') {
      const cellValue = options.value;

      if (!cellValue || typeof cellValue !== 'object') {
        return;
      }

      const totals = options.totalValue as ReconciliationGridSummaryAccumulator;

      if (cellValue.current != null) {
        totals.current += cellValue.current;
        totals.hasCurrent = true;
      }

      if (cellValue.compare != null) {
        totals.compare += cellValue.compare;
        totals.hasCompare = true;
      }

      return;
    }

    if (options.summaryProcess !== 'finalize') {
      return;
    }

    const totals = options.totalValue as ReconciliationGridSummaryAccumulator;
    const cellViewModel = this.buildMetricCellViewModel(
      {
        current: totals.hasCurrent ? totals.current : null,
        compare: totals.hasCompare ? totals.compare : null,
      },
      summaryItem.format,
    );
    const changeLine = cellViewModel.change ? `\n${cellViewModel.change}` : '';

    options.totalValue = `${summaryItem.label}:\n${cellViewModel.primary}${changeLine}`;
  };

  protected buildMetricCellViewModel(
    value: ProductionReconciliationDualValue | string | null | undefined,
    format: 'currency' | 'number',
  ): ReconciliationMetricCellViewModel {
    if (!value || typeof value !== 'object') {
      return { primary: '- / -', change: null };
    }

    const primary = `${this.formatMetricValue(value.current, format)} / ${this.formatMetricValue(value.compare, format)}`;

    if (value.current == null || value.compare == null) {
      return { primary, change: null };
    }

    return {
      primary,
      change: this.formatMetricValue(value.current - value.compare, format),
    };
  }

  protected onReconciliationGridCellPrepared(event: CellPreparedEvent): void {
    if (event.rowType !== 'totalFooter') {
      return;
    }

    const dataField = event.column?.dataField;

    if (!dataField || !this.isMetricDataField(dataField) || !event.cellElement) {
      return;
    }

    const summaryItem = this.gridSummaryTotalItemsSig().find(item => item.dataField === dataField);

    if (!summaryItem) {
      return;
    }

    const columnTotals = this.getColumnTotals(dataField);
    const cellViewModel = this.buildMetricCellViewModel(columnTotals, summaryItem.format);
    event.cellElement.innerHTML = this.buildSummaryMetricCellHtml(summaryItem.label, cellViewModel, columnTotals);
  }

  constructor() {
    this.dashboardContext.paidFileAsOfData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(asOfDate => {
      if (this.hasInitializedSetupFromAsOfDate) {
        return;
      }

      const normalizedAsOfDate = normalizeAsOfDate(asOfDate) ?? getLastPassedFriday();
      const currentYear = Number(normalizedAsOfDate.slice(0, 4));
      const compareYear = currentYear - 1;

      this.appliedSetup = {
        mode: 'ytd',
        currentYear,
        compareYear,
        stopDate: normalizedAsOfDate,
        currentYearPeriods: [getReportingPeriodIndexForDate(currentYear, normalizedAsOfDate)],
      };
      this.draftSetup = this.copySetup(this.appliedSetup);
      this.hasInitializedSetupFromAsOfDate = true;
      void this.loadDashboard();
    });
  }

  protected getDisplayedSetupSummary(): string {
    return this.getSetupSummary(this.isSetupModalVisible ? this.draftSetup : this.appliedSetup);
  }

  protected goToMyReports(): void {
    void this.router.navigate(this.dashboardContext.myReportsRouteCommands);
  }

  protected openSetupModal(): void {
    this.draftSetup = this.copySetup(this.appliedSetup);
    this.isSetupModalVisible = true;
  }

  protected closeSetupModal(): void {
    this.isSetupModalVisible = false;
  }

  protected resetSetupDraft(): void {
    const fallbackStopDate = getLastPassedFriday();
    const currentYear = Number(fallbackStopDate.slice(0, 4));

    this.draftSetup = {
      mode: 'ytd',
      currentYear,
      compareYear: currentYear - 1,
      stopDate: fallbackStopDate,
      currentYearPeriods: [getReportingPeriodIndexForDate(currentYear, fallbackStopDate)],
    };
  }

  protected applySetup(): void {
    const validationResult = this.setupFormComponent?.instance.validate();

    if (validationResult && !validationResult.isValid) {
      return;
    }

    if (this.draftSetup.currentYear === this.draftSetup.compareYear) {
      return;
    }

    if (this.draftSetup.mode === 'range' && !this.draftSetup.currentYearPeriods.length) {
      return;
    }

    this.appliedSetup = this.copySetup(this.draftSetup);
    this.isSetupModalVisible = false;
    void this.loadDashboard();
  }

  protected retryLoad(): void {
    void this.loadDashboard();
  }

  protected readonly onDraftModeChange = (event: { value: ProductionReconciliationPresetMode }): void => {
    this.draftSetup.mode = event.value;

    if (event.value !== 'range') {
      this.cdr.markForCheck();
      return;
    }

    const defaultPeriods = this.draftSetup.currentYearPeriods.length
      ? this.draftSetup.currentYearPeriods
      : [getReportingPeriodIndexForDate(this.draftSetup.currentYear, this.draftSetup.stopDate)];

    this.draftSetup.currentYearPeriods = this.clampPeriodsToAvailable(this.draftSetup.currentYear, defaultPeriods);
    this.cdr.markForCheck();
  };

  protected readonly onDraftCurrentYearChange = (event: { value: number }): void => {
    this.draftSetup.currentYear = event.value;

    if (this.draftSetup.mode === 'ytd') {
      const { month, day } = toDateParts(this.draftSetup.stopDate);
      this.draftSetup.stopDate = this.clampStopDateToCurrentYear(buildIsoDate(event.value, month, day));
    }

    this.draftSetup.currentYearPeriods = this.clampPeriodsToAvailable(
      event.value,
      this.draftSetup.currentYearPeriods.length
        ? this.draftSetup.currentYearPeriods
        : [getReportingPeriodIndexForDate(event.value, this.draftSetup.stopDate)],
    );
    this.cdr.markForCheck();
  };

  protected readonly onDraftCompareYearChange = (event: { value: number }): void => {
    this.draftSetup.compareYear = event.value;
    this.cdr.markForCheck();
  };

  protected readonly onDraftStopDateChange = (event: ValueChangedEvent): void => {
    const nextValue = this.toInputDate(event.value, this.draftSetup.stopDate);
    this.draftSetup.stopDate = this.clampStopDateToCurrentYear(nextValue);
    this.draftSetup.currentYearPeriods = [
      getReportingPeriodIndexForDate(this.draftSetup.currentYear, this.draftSetup.stopDate),
    ];
    this.cdr.markForCheck();
  };

  protected onDraftCurrentYearPeriodListSelectionChanged(event: {
    component?: { option: (name: string) => unknown };
  }): void {
    const selectedKeys = (event.component?.option('selectedItemKeys') as number[] | undefined) ?? [];
    this.updateDraftCurrentYearPeriods(selectedKeys);
  }

  protected readonly handlePeriodDropDownOpened = (): void => {
    this.isPeriodDropDownOpenSig.set(true);
  };

  protected readonly handlePeriodDropDownClosed = (): void => {
    this.isPeriodDropDownOpenSig.set(false);
    this.cdr.markForCheck();
  };

  protected getCurrentYearPeriodsDisplayText(): string {
    const periods = [...this.draftSetup.currentYearPeriods].sort((left, right) => left - right);

    if (!periods.length) {
      return '';
    }

    if (periods.length === 1) {
      return buildReportingPeriodOptionLabel(this.draftSetup.currentYear, periods[0]);
    }

    return `${periods.length} periods selected`;
  }

  protected getDraftStopDateMin(): string {
    return `${this.draftSetup.currentYear}-01-01`;
  }

  protected getDraftStopDateMax(): string {
    return getEndOfYearDate(this.draftSetup.currentYear);
  }

  protected toggleFullScreen = async (container: HTMLDivElement): Promise<void> => {
    if (this.document.fullscreenElement) {
      await this.document.exitFullscreen();
      return;
    }

    this.document
      .querySelectorAll('.ag-full-screen-app')
      .forEach(element => element.classList.remove('ag-full-screen-app'));
    await this.document.documentElement.requestFullscreen();
    container.classList.add('ag-full-screen-app');
  };

  @HostListener('document:fullscreenchange')
  protected fullscreenChanged(): void {
    if (!this.document.fullscreenElement) {
      this.document
        .querySelectorAll('.ag-full-screen-app')
        .forEach(element => element.classList.remove('ag-full-screen-app'));
    }
  }

  protected formatDualMetricCell(
    value: ProductionReconciliationDualValue | string | null | undefined,
    format: 'currency' | 'number',
  ): string {
    const cellViewModel = this.buildMetricCellViewModel(value, format);
    const changeLine = cellViewModel.change ? `\n${cellViewModel.change}` : '';

    return `${cellViewModel.primary}${changeLine}`;
  }

  private getColumnTotals(dataField: string): ProductionReconciliationDualValue {
    let current = 0;
    let compare = 0;
    let hasCurrent = false;
    let hasCompare = false;

    this.gridDataSourceSig().forEach(row => {
      const cellValue = row[dataField];

      if (!cellValue || typeof cellValue !== 'object') {
        return;
      }

      if (cellValue.current != null) {
        current += cellValue.current;
        hasCurrent = true;
      }

      if (cellValue.compare != null) {
        compare += cellValue.compare;
        hasCompare = true;
      }
    });

    return {
      current: hasCurrent ? current : null,
      compare: hasCompare ? compare : null,
    };
  }

  private isMetricDataField(dataField: string): boolean {
    return dataField.startsWith('period');
  }

  private buildSummaryMetricCellHtml(
    label: string,
    cellViewModel: ReconciliationMetricCellViewModel,
    totals: ProductionReconciliationDualValue,
  ): string {
    const changeHtml = cellViewModel.change
      ? `<div class="reconciliation-dashboard__metric-change" title="Change">Change: ${cellViewModel.change}</div>`
      : '';
    const changePercentHtml = this.buildSummaryChangePercentHtml(totals);

    return `
      <div class="reconciliation-dashboard__summary-cell">
        <div class="reconciliation-dashboard__summary-label">${label}:</div>
        <div class="reconciliation-dashboard__metric-values">${cellViewModel.primary}</div>
        ${changeHtml}
        ${changePercentHtml}
      </div>
    `;
  }

  private buildSummaryChangePercentHtml(totals: ProductionReconciliationDualValue): string {
    if (totals.current == null || totals.compare == null || totals.compare === 0) {
      return '';
    }

    const change = totals.current - totals.compare;
    const changePercent = (change / totals.compare) * 100;

    return `<div class="reconciliation-dashboard__metric-change">${this.formatPercentValue(changePercent)}</div>`;
  }

  private formatPercentValue(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
      signDisplay: 'exceptZero',
    }).format(value / 100);
  }

  private async loadDashboard(): Promise<void> {
    this.isLoadingSig.set(true);
    this.loadErrorMessageSig.set(null);
    this.cdr.markForCheck();

    try {
      const dashboardData = await this.reconciliationService.loadDashboardData(this.appliedSetup);
      this.dashboardDataSig.set(dashboardData);
    } catch (error) {
      console.log(error);
      this.dashboardDataSig.set(null);
      this.loadErrorMessageSig.set('Unable to load production reconciliation data. Please try again.');
    } finally {
      this.isLoadingSig.set(false);
      this.cdr.markForCheck();
    }
  }

  private createDefaultSetup(): ProductionReconciliationDashboardSetup {
    const fallbackStopDate = getLastPassedFriday();
    const currentYear = Number(fallbackStopDate.slice(0, 4));

    return {
      mode: 'ytd',
      currentYear,
      compareYear: currentYear - 1,
      stopDate: fallbackStopDate,
      currentYearPeriods: [getReportingPeriodIndexForDate(currentYear, fallbackStopDate)],
    };
  }

  private copySetup(setup: ProductionReconciliationDashboardSetup): ProductionReconciliationDashboardSetup {
    return {
      ...setup,
      currentYearPeriods: [...setup.currentYearPeriods],
    };
  }

  private getSetupSummary(setup: ProductionReconciliationDashboardSetup): string {
    if (setup.mode === 'range') {
      const periods = [...setup.currentYearPeriods].sort((left, right) => left - right);

      if (!periods.length) {
        return 'Range (no periods selected)';
      }

      if (periods.length === 1) {
        const currentRange = getReportingPeriodRange(setup.currentYear, periods[0]);
        const compareRange = getReportingPeriodRange(setup.compareYear, periods[0]);

        return `Range ${formatCompactDateRange(currentRange.start, currentRange.end)} vs ${formatCompactDateRange(compareRange.start, compareRange.end)}`;
      }

      return `Range ${periods.length} weeks (${periods.map(periodIndex => `Week ${periodIndex}`).join(', ')})`;
      // return `Range ${periods.length} weeks selected`;
    }

    return `YTD ${setup.currentYear} vs ${setup.compareYear} through ${this.formatMonthDay(setup.stopDate)}`;
  }

  private formatMonthDay(isoDate: string): string {
    const [, month, day] = isoDate.split('-');
    return `${month}/${day}`;
  }

  private clampStopDateToCurrentYear(isoDate: string): string {
    const min = this.getDraftStopDateMin();
    const max = this.getDraftStopDateMax();

    if (isoDate < min) {
      return min;
    }

    if (isoDate > max) {
      return max;
    }

    return isoDate;
  }

  private updateDraftCurrentYearPeriods(nextPeriods: number[]): void {
    const normalizedPeriods = this.clampPeriodsToAvailable(this.draftSetup.currentYear, nextPeriods);
    const currentPeriods = this.draftSetup.currentYearPeriods;

    if (
      currentPeriods.length === normalizedPeriods.length &&
      currentPeriods.every((periodId, index) => periodId === normalizedPeriods[index])
    ) {
      return;
    }

    this.draftSetup.currentYearPeriods = normalizedPeriods;

    if (!this.isPeriodDropDownOpenSig()) {
      this.cdr.markForCheck();
    }
  }

  private clampPeriodsToAvailable(year: number, periods: number[]): number[] {
    const latestPeriodIndex = getLatestCompletedReportingPeriodIndex(year);
    const normalizedPeriods = [...new Set(periods)]
      .filter(periodIndex => periodIndex >= 1 && periodIndex <= latestPeriodIndex)
      .sort((left, right) => left - right);

    if (normalizedPeriods.length) {
      return normalizedPeriods;
    }

    return [Math.max(1, latestPeriodIndex)];
  }

  private toInputDate(value: DateBoxValue, fallback: string): string {
    if (!value) {
      return fallback;
    }

    if (value instanceof Date) {
      return toIsoDate(value);
    }

    return `${value}`.slice(0, 10);
  }

  private buildCarrierColumn(): ReconciliationGridColumn[] {
    return [
      {
        dataField: ProductionReconciliationDashboardColumnKey.carrierName,
        caption: 'Carrier',
        dataType: 'string',
        fixed: true,
        fixedPosition: 'left',
        minWidth: 180,
        sortIndex: 0,
        sortOrder: 'asc',
      },
    ];
  }

  private formatMetricValue(value: number | null | undefined, format: 'currency' | 'number'): string {
    if (value == null) {
      return '-';
    }

    if (format === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(value);
    }

    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value);
  }

  protected readonly Messages = Messages;
}
